import { zodResolver } from "@hookform/resolvers/zod";
import { notifications } from "@marketplace/ui/notifications";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import {
  type SubmitHandler,
  useForm,
  type UseFormReturn,
  useWatch
} from "react-hook-form";

import type { CreateAutomationRequestDto } from "../../dtos/create-automation-request-dto";
import { useUpdateAutomationMutation } from "../../hooks/mutations/use-update-automation-mutation";
import { useGetAutomationDetailQuery } from "../../hooks/queries/use-get-automation-detail-query";
import { mapDetailToFormDefaults } from "../../utils/automation-mapper";
import {
  type AutomationFormSchema,
  useAutomationFormSchema
} from "../use-automation-form-schema";

const route = getRouteApi("/products/automation/$slug/edit");

const DEFAULT_VALUES: AutomationFormSchema = {
  product_id: "",
  product_name: "",
  host: "",
  host_auth: "",
  auth_type: "",
  auth_header_name: "",
  auth_value: "",
  token_response_field: "",
  delivery_type: "single",
  endpoints: [
    {
      step_type: "",
      step_name: "",
      content_type: "",
      method: "",
      endpoint_provisioning: "",
      body_fill_customer: [
        {
          title: "",
          key: "",
          value: "",
          target_type: "body",
          data_type: "string"
        }
      ],
      body_static_system: [
        {
          title: "",
          key: "",
          value: "",
          target_type: "body",
          data_type: "string"
        }
      ],
      body_static_value: [
        {
          title: "",
          key: "",
          value: "",
          target_type: "body",
          data_type: "string"
        }
      ],
      next_endpoint_fields: [
        {
          title: "",
          key: "",
          value: "",
          target_type: "body",
          data_type: "string"
        }
      ]
    }
  ]
};

const mapFormToApi = (
  data: AutomationFormSchema
): CreateAutomationRequestDto => {
  const authTypeMap: Record<string, string> = {
    basic: "custom_header",
    "x-header-secret": "custom_header",
    bearer: "bearer"
  };

  return {
    product_id: data.product_id,
    name: data.product_name,
    host: data.host,
    host_auth: data.host_auth,
    auth_type: authTypeMap[data.auth_type] ?? data.auth_type,
    auth_header_name: data.auth_header_name,
    auth_value_prefix: data.auth_value,
    token_response_field: data.token_response_field,
    steps: data.endpoints.map((ep, i) => ({
      step_order: i + 1,
      step_type: ep.step_type || "api_call",
      name: ep.step_name || `Step ${i + 1}`,
      method: ep.method,
      endpoint: ep.endpoint_provisioning,
      content_type: ep.content_type || "application/json",
      mappings: [
        ...(ep.body_fill_customer ?? [])
          .filter((f) => f.title.trim() && f.key.trim())
          .map((f) => ({
            mapping_type: "dynamic",
            target_type: f.target_type || "body",
            title: f.title,
            key: f.key,
            value: f.value,
            ...(f.data_type && f.data_type !== "string"
              ? { value_type: f.data_type }
              : {})
          })),
        ...(ep.body_static_system ?? [])
          .filter((f) => f.title.trim() && f.key.trim())
          .map((f) => ({
            mapping_type: "static",
            target_type: f.target_type || "body",
            title: f.title,
            key: f.key,
            value: f.value,
            ...(f.data_type && f.data_type !== "string"
              ? { value_type: f.data_type }
              : {})
          })),
        ...(ep.body_static_value ?? [])
          .filter((f) => f.title.trim() && f.key.trim())
          .map((f) => ({
            mapping_type: "system",
            target_type: f.target_type || "body",
            title: f.title,
            key: f.key,
            value: f.value,
            ...(f.data_type && f.data_type !== "string"
              ? { value_type: f.data_type }
              : {})
          })),
        ...(ep.next_endpoint_fields ?? [])
          .filter((f) => f.title.trim() && f.key.trim())
          .map((f) => ({
            mapping_type: "response",
            target_type: "body",
            title: f.title,
            key: f.key,
            value: f.value,
            ...(f.data_type && f.data_type !== "string"
              ? { value_type: f.data_type }
              : {})
          }))
      ]
    }))
  };
};

type UseEditAutomationFormReturn = {
  formMethods: UseFormReturn<AutomationFormSchema>;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  onDiscard: () => void;
  isPending: boolean;
  isLoading: boolean;
};

export const useEditAutomationForm = (): UseEditAutomationFormReturn => {
  const { slug: automationId } = route.useParams();
  const navigate = useNavigate();
  const schema = useAutomationFormSchema();

  const { data: detailData, isLoading } = useGetAutomationDetailQuery({
    queryParams: { id: automationId },
    options: { enabled: !!automationId }
  });

  const { register, control, handleSubmit, ...methods } =
    useForm<AutomationFormSchema>({
      resolver: zodResolver(schema),
      defaultValues: DEFAULT_VALUES
    });

  const { mutateAsync, isPending } = useUpdateAutomationMutation();

  const watchedAuthType = useWatch({ control, name: "auth_type" });

  const lastResetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (watchedAuthType === "basic" || watchedAuthType === "bearer") {
      methods.setValue("auth_header_name", "Authorization");
      return;
    }

    if (watchedAuthType === "x-header-secret") {
      const prev = methods.getValues("auth_header_name");
      if (prev === "Authorization") {
        methods.setValue("auth_header_name", "");
      }
    }
  }, [watchedAuthType, methods]);

  useEffect(() => {
    const data = detailData?.data;
    if (data && !isLoading && lastResetIdRef.current !== data.id) {
      methods.reset(mapDetailToFormDefaults(data));
      lastResetIdRef.current = data.id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailData, isLoading]);

  const onSubmit: SubmitHandler<AutomationFormSchema> = async (data) => {
    try {
      const payload = mapFormToApi(data);
      const res = await mutateAsync({ id: automationId, payload });

      if (res && !res.errors) {
        notifications.show({
          message: "Automation updated successfully"
        });
        navigate({ to: "/products/automation" });
      }
    } catch {
      notifications.show({
        variant: "error",
        message: "Failed to update automation"
      });
    }
  };

  const onDiscard = () => {
    navigate({ to: "/products/automation" });
  };

  const formMethods: UseFormReturn<AutomationFormSchema> = {
    ...methods,
    register,
    control,
    handleSubmit
  };

  return {
    formMethods,
    onSubmit: handleSubmit(onSubmit),
    onDiscard,
    isPending,
    isLoading
  };
};
